import * as color from "../../constants/color";
import * as searchInputTypes from "../../constants/searchInputTypes";
import * as breakpoints from "../../constants/breakpoints";
import * as texchangePropTypes from "../../texchangePropTypes";
import * as zIndexes from "../../constants/zIndexes";
import React, { Component } from "react";
import PropTypes from "prop-types";
import { StyleSheet, css } from "aphrodite";
import searchStreamlineIcon from "../../images/SVGs/SearchStreamline.svg";
import ErrorMessage from "../text/ErrorMessage";

const VERTICAL_PADDING_DIFFERENCE = 12;
const ADDITIONAL_PADDING = 42;
const ITEM_HEIGHT = 53;
const SHOW_ABOVE_BOTTOM_BUFFER = 120;
const SHOW_ABOVE_SPACE_TOLERANCE = 48;

const SEARCH_WAIT_INTERVAL = 500;

const placeholder = {
    color: color.searchInput,
    fontWeight: 400,
};

const placeholderNoIcon = {
    color: color.darkGray5,
    fontSize: 16,
    fontWeight: 400,
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        position: "relative",
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-start",
        alignItems: "stretch",
        outline: 0,
    },
    input: {
        width: "100%",
        backgroundColor: color.white,
        border: "none",
        borderRadius: 6,
        color: color.searchInput,
        boxShadow:
            "0 2px 2px 0 rgba(0,0,0,0.14), 0 3px 1px -2px rgba(0,0,0,0.12), 0 1px 5px 0 rgba(0,0,0,0.2)",
        fontSize: 20,
        fontWeight: 400,
        lineHeight: "22px",
        outline: "none",
        padding: "22px 22px",
        margin: "10px 0px",

        "::-webkit-input-placeholder": placeholder,
        "::-moz-placeholder": placeholder,
        "::-ms-input-placeholder": placeholder,
        "::-placeholder": placeholder,

        ":focus": {
            boxShadow: "none",
            border: "solid",
            borderColor: color.lightOrange,
            borderWidth: 2,
        },

        [breakpoints.tabletAndDown]: {
            letterSpacing: 0.17,
        },

        [breakpoints.desktopOnly]: {},
    },
    inputText: {
        padding: "20px 16px",
    },
    inputTextNoIcon: {
        boxShadow: "none",
        border: "solid",
        borderWidth: 1,
        borderRadius: 4,
        borderColor: color.mediumGray,
        color: color.darkestGray,
        fontSize: 16,
        fontWeight: 400,
        lineHeight: "22px",
        outline: "none",
        padding: "18px 16px",
        margin: "10px 0px",

        "::-webkit-input-placeholder": placeholderNoIcon,
        "::-moz-placeholder": placeholderNoIcon,
        "::-ms-input-placeholder": placeholderNoIcon,
        "::-placeholder": placeholderNoIcon,
    },
    inputTextNoIconShort: {
        boxShadow: "none",
        border: "solid",
        borderWidth: 1,
        borderRadius: 4,
        borderColor: color.mediumGray,
        color: color.darkestGray,
        fontSize: 14,
        fontWeight: 400,
        lineHeight: "18px",
        outline: "none",
        padding: "9px 8px",
        margin: "5px 0px",

        "::-webkit-input-placeholder": placeholderNoIcon,
        "::-moz-placeholder": placeholderNoIcon,
        "::-ms-input-placeholder": placeholderNoIcon,
        "::-placeholder": placeholderNoIcon,
    },
    hasValue: {
        paddingTop: 24,
        paddingBottom: 12,
    },
    title: {
        position: "absolute",
        top: 18,
        left: 12,
        fontSize: 13,
        color: color.darkGray,
    },
    disabled: {
        color: color.darkestBlueAlpha30,
    },
    withValidationError: {
        color: color.darkRed,
    },
    validationMessage: {
        position: "absolute",
        top: 16,
        right: 8,
        pointerEvents: "none",
    },
    icon: {
        position: "absolute",
        top: 31,
        right: 48,
    },
    iconText: {
        top: 28,
    },
    searchResultsContainer: {
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-start",
        alignItems: "stretch",
        height: "100%",
    },
    borderContainer: {
        // transition: "height 0.3s",
        backgroundColor: color.white,
        borderTop: `1px solid ${color.lightGray2}`,
        position: "absolute",
        right: 0,
        left: 10,
        maxHeight: 485,
        overflow: "hidden",
        boxShadow:
            "0 4px 5px 0 rgba(0,0,0,0.14), 0 8px 10px 0 rgba(0,0,0,0.12), 0 2px 4px -1px rgba(0,0,0,0.2)",
        width: "calc(100% - 20px)",
    },
    borderContainerNoIcon: {
        // width: "auto",
    },
    resultItem: {
        cursor: "pointer",
        border: "none",
        alignSelf: "stretch",
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
        backgroundColor: color.transparent,
        textAlign: "left",
        paddingLeft: 16,
        paddingRight: 16,
        paddingTop: 16,
        paddingBottom: 16,
        outline: 0,
        borderBottom: `1px solid ${color.lightGray2}`,
        color: color.darkestGray,
        ":hover": {
            backgroundColor: color.mediumRed,
            color: color.white,
        },
        [breakpoints.desktopOnly]: {},
    },
    active: {
        backgroundColor: color.mediumRed,
        color: color.white,
    },
    resultItemText: {
        fontSize: 18,
        fontWeight: 400,
    },
});

export default class SearchInput extends Component {
    static propTypes = {
        aStyles: texchangePropTypes.aphroditeStyles,
        placeholder: PropTypes.string,
        hideLabel: PropTypes.bool,
        value: PropTypes.string,
        extraId: PropTypes.number,
        search: PropTypes.func.isRequired,
        onItemSelected: PropTypes.func.isRequired,
        type: PropTypes.string.isRequired,
        disabled: PropTypes.bool,
        validationMessage: PropTypes.oneOfType([
            PropTypes.arrayOf(PropTypes.string),
            PropTypes.string,
        ]),
        onTextChanged: PropTypes.func,
        revertOnBlur: PropTypes.bool,
        tabIndex: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        showResultsAbove: PropTypes.bool,
    };

    static defaultProps = {
        type: "search",
        revertOnBlur: false,
    };

    state = {
        value: "",
        extraId: null,
        searchResults: [],
        activeOption: -1,
        isActive: false,
        open: true,
        popupHeight: null,
        inputHeight: null,
    };

    constructor(props) {
        super(props);

        this._containerRef = React.createRef();
        this._inputRef = React.createRef();
        this._popupRef = React.createRef();
    }

    UNSAFE_componentWillMount() {
        this._timer = null;
    }

    componentDidMount() {
        this._mounted = true;

        const { value } = this.props;
        if (value) {
            this.setState({ value });
        }

        setTimeout(() => {
            if (!this._mounted) {
                return;
            }

            this.setState({
                popupHeight: this._getPopupHeight(this._inputRef.current.clientHeight, 0),
                inputHeight: this._inputRef.current.clientHeight,
            });
        });
    }

    componentWillUnmount() {
        this._mounted = false;
    }

    _hideResults = () =>
        this.setState({ open: false, activeOption: -1, popupHeight: 0, isActive: false });

    _onFocus = () => this.setState({ isActive: true });

    _onBlur = e => {
        const nextTarget = e && e.relatedTarget;
        if (
            nextTarget &&
            this._containerRef.current &&
            this._containerRef.current.contains(nextTarget)
        ) {
            return;
        }

        if (this.props.revertOnBlur) {
            this.setState({ value: this.props.value || "" });
        }

        this._hideResults();
    };

    _onSelect = resultItem => {
        if (resultItem && resultItem.label) {
            this.props.onItemSelected(resultItem);
            this.setState({ value: resultItem.label });
            this._hideResults();
        }
    };

    _renderResults = () => (
        <div
            ref={this._popupRef}
            className={css(styles.searchResultsContainer)}
            style={
                this.state.open
                    ? {
                          overflowY: "auto",
                      }
                    : null
            }
        >
            {this.state.searchResults.map(this._renderResultItem)}
        </div>
    );

    _runSearch = term => {
        const { extraId } = this.props;

        if (!term) {
            this.setState({ searchResults: [] });
            return;
        }

        this.props.search(term, extraId).then(response => {
            const open = response.length > 0;
            this.setState({
                popupHeight: this._getPopupHeight(this.state.inputHeight, response.length),
                searchResults: response,
                open,
            });
        });
    };

    _handleTextChange = e => {
        this.setState({ isActive: true });
        if (this._timer) {
            clearTimeout(this._timer);
        }

        const { value } = e.target;
        if (value !== this.state.value) {
            this.setState({ value, activeOption: -1 });
            this._timer = setTimeout(() => {
                this._runSearch(value.trim());
            }, SEARCH_WAIT_INTERVAL);
        }

        if (this.props.onTextChanged) {
            this.props.onTextChanged(value);
        }
    };

    _getPopupHeight = (inputHeight, length) => {
        const defaultPopupHeight = // Calculate initial popup height based on result count before checking available space
            length <= 1 // If 1 or fewer results
                ? ITEM_HEIGHT + VERTICAL_PADDING_DIFFERENCE // Show single item height
                : length * ITEM_HEIGHT + VERTICAL_PADDING_DIFFERENCE < 485 // Else if results fit under max height
                ? length * ITEM_HEIGHT + VERTICAL_PADDING_DIFFERENCE // Show exact height needed
                : 485; // Else cap at max height (will scroll)

        if (!this._popupRef.current) {
            return defaultPopupHeight;
        }

        if (!this._inputRef.current) {
            return defaultPopupHeight;
        }

        const inputRect = this._inputRef.current.getBoundingClientRect();
        const appHeight = document.body.classList.contains("no-scroll")
            ? window.innerHeight
            : document.body.clientHeight;
        const spaceBelow = appHeight - inputRect.bottom - ADDITIONAL_PADDING; // Calculate space remaining below the input
        const spaceAbove = inputRect.top - ADDITIONAL_PADDING; // Calculate space remaining above the input
        const showResultsAbove =
            this.props.showResultsAbove || this._shouldShowResultsAbove(defaultPopupHeight); // Determine positioning direction; pass default height to avoid circular dependency
        const availableHeight = showResultsAbove ? spaceAbove : spaceBelow; // Use available space from the direction results will render
        const minPopupHeight = ITEM_HEIGHT + VERTICAL_PADDING_DIFFERENCE;

        return Math.max(Math.min(defaultPopupHeight, availableHeight), minPopupHeight); // Enforce minimum and cap at available space
    };

    _shouldShowResultsAbove = (popupHeightOverride = null) => {
        if (!this._inputRef.current) {
            return false;
        }

        const inputRect = this._inputRef.current.getBoundingClientRect();
        const appHeight = document.body.classList.contains("no-scroll")
            ? window.innerHeight
            : document.body.clientHeight;

        const popupHeight = // Accept height override parameter to avoid circular dependency with _getPopupHeight
            popupHeightOverride ||
            this.state.popupHeight ||
            ITEM_HEIGHT + VERTICAL_PADDING_DIFFERENCE;
        const requiredSpace = popupHeight + ADDITIONAL_PADDING + SHOW_ABOVE_BOTTOM_BUFFER;
        const spaceBelow = appHeight - inputRect.bottom;
        const spaceAbove = inputRect.top;

        return spaceBelow <= requiredSpace && spaceAbove + SHOW_ABOVE_SPACE_TOLERANCE >= spaceBelow;
    };

    _onKeyDown = e => {
        const { searchResults, activeOption } = this.state;

        if (e.keyCode === 13) {
            // Enter
            this.setState({
                activeOption: 0,
                open: false,
            });
            this._onSelect(searchResults[activeOption]);
        } else if (e.keyCode === 38) {
            // Up arrow
            if (activeOption > 0) {
                this.setState({ activeOption: activeOption - 1 });
            }
        } else if (e.keyCode === 40) {
            // Down arrow
            if (activeOption - 1 < searchResults.length) {
                this.setState({ activeOption: activeOption + 1 });
            }
        } else if (e.keyCode === 27) {
            // Esc
            this._hideResults();
        }
    };

    _renderResultItem = (resultItem, index) => (
        <button
            key={resultItem.value}
            className={css(
                styles.resultItem,
                index === this.state.activeOption ? styles.active : null,
            )}
            type="button"
            // eslint-disable-next-line react/jsx-no-bind
            onClick={() => this._onSelect(resultItem)}
        >
            <div className={css(styles.resultItemText)}>{resultItem.label}</div>
        </button>
    );

    render() {
        const {
            validationMessage,
            aStyles,
            placeholder: inputPlaceholder,
            hideLabel,
            type,
            disabled,
            tabIndex,
            showResultsAbove,
        } = this.props;
        const numericTabIndex = Number(tabIndex) || 0;
        const zIndexTabOffset = numericTabIndex > 1000 ? numericTabIndex % 1000 : numericTabIndex;
        const showResultsAbovePosition = showResultsAbove || this._shouldShowResultsAbove();

        return (
            <div
                ref={this._containerRef}
                className={css(styles.container, aStyles)}
                onFocus={this._onFocus}
                onMouseDown={this._onFocus}
                onBlur={this._onBlur}
                style={{
                    zIndex: zIndexes.SEARCH - zIndexTabOffset + (this.state.isActive ? 2000 : 0),
                }}
            >
                {validationMessage ? (
                    <ErrorMessage aStyles={styles.validationMessage}>
                        {validationMessage}
                    </ErrorMessage>
                ) : null}

                <input
                    placeholder={inputPlaceholder}
                    className={css(
                        styles.input,
                        type === searchInputTypes.text ? styles.inputText : null,
                        type === searchInputTypes.textNoIcon ? styles.inputTextNoIcon : null,
                        type === searchInputTypes.textNoIconShort
                            ? styles.inputTextNoIconShort
                            : null,
                        disabled ? styles.disabled : null,
                        validationMessage && this.props.value === ""
                            ? styles.withValidationError
                            : null,
                        this.props.value !== "" && !hideLabel ? styles.hasValue : null,
                    )}
                    onKeyDown={this._onKeyDown}
                    onChange={this._handleTextChange}
                    value={this.state.value}
                    ref={this._inputRef}
                    disabled={disabled}
                    type="search"
                    tabIndex={this.props.tabIndex}
                />

                {this.state.value !== "" && this.state.value !== null && !hideLabel ? (
                    <div className={css(styles.title)}>{inputPlaceholder}</div>
                ) : null}

                {this.props.type === searchInputTypes.text ? (
                    <img
                        src={searchStreamlineIcon}
                        alt="search"
                        className={css(
                            styles.icon,
                            this.props.type === "text" ? styles.iconText : null,
                        )}
                    />
                ) : null}
                {this.state.open ? (
                    <div
                        className={css(
                            styles.borderContainer,
                            this.props.type === searchInputTypes.textNoIcon ||
                                this.props.type === searchInputTypes.textNoIconShort
                                ? styles.borderContainerNoIcon
                                : null,
                        )}
                        style={{
                            visibility:
                                this.state.searchResults.length === 0 ? "hidden" : "visible",
                            top: showResultsAbovePosition
                                ? undefined
                                : this.state.open
                                ? this.state.inputHeight + 10
                                : 0,
                            bottom: showResultsAbovePosition
                                ? this.state.inputHeight + 10
                                : undefined,
                            height: this.state.open ? this.state.popupHeight : null,
                        }}
                    >
                        {this._renderResults()}
                    </div>
                ) : null}
            </div>
        );
    }
}
